/**
 * The team of a workspace (plane/docs/team-proposal.md): an assistant with `delegates` is the
 * orchestrator, the assistants it names its specialists, which people reach only through it.
 * The rules are those of the backend (ConfigurationContent.teamProblems), checked here first so
 * that a mistake shows next to its field.
 */

/** The ids an assistant delegates to; empty for one that leads no team. */
export const delegatesOf = (params, id) => params?.assistants?.[id]?.delegates ?? [];

/** The ids of the specialists of a workspace: the assistants another one delegates to. */
export function specialistsOf(params) {
  return new Set(Object.keys(params?.assistants ?? {}).flatMap((id) => delegatesOf(params, id)));
}

/** The id of the orchestrator an assistant answers to, or undefined outside a team. */
export const orchestratorOf = (params, id) => Object.keys(params?.assistants ?? {}).find((other) => delegatesOf(params, other).includes(id));

/**
 * The ids of the assistants in the order a team reads: each orchestrator followed by its
 * specialists, then the assistants outside a team, each group by id.
 */
export function teamOrder(params) {
  const ids = Object.keys(params?.assistants ?? {}).sort();
  const specialists = specialistsOf(params);
  const ordered = [];
  for (const id of ids) {
    if (delegatesOf(params, id).length > 0) {
      ordered.push(id, ...delegatesOf(params, id).filter((delegate) => ids.includes(delegate) && !ordered.includes(delegate)));
    }
  }
  return [...ordered, ...ids.filter((id) => !ordered.includes(id) && !specialists.has(id)), ...ids.filter((id) => !ordered.includes(id) && specialists.has(id))];
}

/**
 * The errors of the team of params, by field key (`assistants.<id>.delegates`,
 * `assistants.<id>.description`); empty for a valid team and for a workspace without one.
 *
 * @returns {Record<string, string>}
 */
export function teamErrors(params) {
  const assistants = params?.assistants ?? {};
  const errors = {};
  for (const id of Object.keys(assistants)) {
    for (const delegate of delegatesOf(params, id)) {
      const key = `assistants.${id}.delegates`;
      if (delegate === id) {
        errors[key] = 'An assistant cannot delegate to itself.';
      } else if (!(delegate in assistants)) {
        errors[key] = `${delegate} is not an assistant of the workspace.`;
      } else if (delegatesOf(params, delegate).length > 0) {
        errors[key] = `${delegate} has delegates of its own: a team has one level only.`;
      } else if (!assistants[delegate].description?.trim()) {
        errors[`assistants.${delegate}.description`] = `${assistants[id].agentName || id} reads it to choose this assistant: write one.`;
      }
    }
  }
  return errors;
}

/** params without `id` among the delegates of any assistant: what removing that assistant leaves. */
export function withoutDelegate(params, id) {
  const assistants = Object.fromEntries(
    Object.entries(params?.assistants ?? {}).map(([other, values]) => {
      if (!values?.delegates?.includes(id)) {
        return [other, values];
      }
      const { delegates, ...rest } = values;
      const left = delegates.filter((delegate) => delegate !== id);
      return [other, left.length > 0 ? { ...rest, delegates: left } : rest];
    }),
  );
  return { ...params, assistants };
}
